import { AutoCompleteController } from '@components/react-hook-components'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import EditDialog from '../components/EditDialog'
import type { Feature } from '../types'
import { FeatureValueInputAdapter } from './FeatureValueInputs/FeatureValueInputAdapter'

type AddFeatureFormData = {
  featureName: string
  value: string | number | boolean
}

type FeatureDialogProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (feature: Feature) => void
  availableFeatures: Feature[]
  excludedFeatures?: Feature[]
}

export const FeatureDialog = ({ isOpen, onClose, onSave, availableFeatures = [], excludedFeatures = [] }: FeatureDialogProps) => {
  const useLocalState = !!onSave

  const existingFeatures = excludedFeatures.map((f) => f.id)
  const availableOptions = availableFeatures
    .filter((feature) => !existingFeatures.includes(feature.id))
    .map((feature) => ({
      value: feature.id,
      label: feature.name,
    }))

  const form = useForm<AddFeatureFormData>({
    defaultValues: {
      featureName: '',
      value: '',
    },
    mode: 'onChange',
  })
  const featureNameValue = useWatch({ control: form.control, name: 'featureName' })
  const selectedFeature = availableFeatures.find((f) => f.id === featureNameValue)

  useEffect(() => {
    if (selectedFeature) {
      let defaultValue: string | number | boolean = ''

      if (selectedFeature.type === 'boolean') {
        defaultValue = selectedFeature.defaultValue ?? false
      } else if (selectedFeature.type === 'integer') {
        defaultValue = selectedFeature.defaultValue ?? 0
      } else {
        defaultValue = selectedFeature.defaultValue ?? ''
      }
      form.setValue('value', defaultValue)
    }
  }, [selectedFeature, form])

  const handleSubmit = (data: AddFeatureFormData) => {
    const featureToAdd = {
      ...availableFeatures.find((f) => f.id === data.featureName),
      value: data.value,
    } as Feature

    if (useLocalState) {
      if (onSave) {
        onSave(featureToAdd)
      }
    }
    form.reset()
    onClose()
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  if (availableFeatures.length === 0 || !isOpen) {
    return null
  }

  return (
    <EditDialog
      isOpen={isOpen}
      title="Add Feature"
      onClose={handleClose}
      isDisabled={!form.formState.isValid || !featureNameValue}
      onSave={form.handleSubmit(handleSubmit)}
    >
      <AutoCompleteController
        control={form.control}
        name="featureName"
        options={availableOptions}
        placeholderText="Select feature"
        label="Feature"
      />
      <FeatureValueInputAdapter control={form.control} name="value" selectedFeature={selectedFeature} />
    </EditDialog>
  )
}
